import React from 'react';
import { Card, CardContent, Chip, CircularProgress, Grid, IconButton, InputAdornment, Link, ListItemIcon, ListItemText, MenuItem, Modal, Typography, Box, Avatar, List, ListItem, ListItemAvatar } from '@mui/material';
import { getContacts } from 'src/services/contact.service';
import config from 'src/config';
import Swal from 'sweetalert2';
import { getComments, addComment } from 'src/services/comment.service';
import { formatDate } from 'src/methods/common.methods';
import AddIcon from '@mui/icons-material/Add';
import CommentIcon from '@mui/icons-material/Comment';
import SendIcon from '@mui/icons-material/Send';
import { MentionsTextField } from '@jackstenglein/mui-mentions';

// ----------------------------------------------------------------------

class Comment extends React.Component {

	constructor(props) {
		super(props);

		this.commentsListRef = React.createRef();

		this.state = {
			view_type: this.props.view_type || 'chip',
			is_modal_open: props.is_modal_open || false,
			loading: false,
			contacts: [],
			comments: [],
			newComment: '',
			logged_in_username: localStorage.getItem('username'),
			sending_message: false
		}
	}

	// Parse @[Name](id) mentions in comment text and render them as clickable elements
	renderMentions(text) {
		if (!text) {
			return text;
		}

		const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
		const parts = [];
		let lastIndex = 0;
		let match;

		while ((match = mentionRegex.exec(text)) !== null) {
			if (!match) break;

			const mentionName = match[1];
			const mentionId = match[2];

			// Add text before the mention
			if (match.index > lastIndex) {
				parts.push(text.slice(lastIndex, match.index));
			}
			// Add the mention element
			parts.push(
				<Link
					key={`mention-${mentionId}`}
					component="span"
					sx={{
						color: 'primary.main',
						textDecoration: 'none',
						fontWeight: 600,
						// cursor: 'pointer',
						// '&:hover': { textDecoration: 'underline' }
					}}
					// onClick={() => {
					// 	// Navigate to user profile or handle mention click
					// 	console.log('Mention clicked:', mentionId);
					// }}
				>
					@{mentionName}
				</Link>
			);
			lastIndex = mentionRegex.lastIndex;
		}

		// Add remaining text after last mention
		if (lastIndex < text.length) {
			parts.push(text.slice(lastIndex));
		}

		return parts;
	}

	async populateContacts(search='') {
        let contactsRes = await getContacts({ search });
        if (contactsRes.error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: contactsRes.error
            });
            return []
        }
        const contacts = contactsRes?.contacts?.map(contact => ({ id: contact.user._id, display: (contact.user.nick_name || contact.user.name || contact.user.username.split('@')[0]) })) || [];
		console.log(contacts);
		this.setState({ contacts });
        return contacts;
    }


	async fetchComments() {

		const query = {}

		if (this.props?.file?._id) {
			query.file = this.props?.file?._id;
		}
		if (this.props?.container?._id) {
			query.container = this.props?.container?._id;
		}

		const commentsRes = await getComments(query);
		if (!commentsRes) {
			return;
		}
		if (commentsRes.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: commentsRes.error,
				confirmButtonText: 'Ok',
				confirmButtonColor: config.primaryColor
			});
			return;
		}

		await this.setState({
			comments: (commentsRes.comments || []).reverse(),
			loading: false
		});

	}

	async componentDidMount() {
		await this.populateContacts();
		if (this.props.is_modal_open) {
			this.fetchComments();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.is_modal_open !== this.props.is_modal_open && this.props.is_modal_open) {
			this.fetchComments();
		}
		if (prevState.comments !== this.state.comments && !this.state.loading) {
			this.scrollCommentsToBottom();
		}
	}

	scrollCommentsToBottom() {
		if (this.commentsListRef.current) {
			this.commentsListRef.current.scrollTop = this.commentsListRef.current.scrollHeight;
		}
	}

	async postComment(comment) {

		this.setState({ sending_message: true });
		const payload = {
			text: comment
		};
		if (this.props?.file?._id) {
			payload.file = this.props?.file?._id;
		}
		if (this.props?.container?._id) {
			payload.container = this.props?.container?._id;
		}
		const addCommentRes = await addComment(payload);
		
		if (addCommentRes.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: addCommentRes.error,
				confirmButtonText: 'Ok',
				confirmButtonColor: config.primaryColor
			});
			return;
		}

		await this.fetchComments();
		this.setState({ newComment: '', sending_message: false });
	}

	handleCommentChange = (value) => {
		this.setState({ newComment: value });
	}

	handleCommentKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			if (this.state.newComment.trim()) {
				this.postComment(this.state.newComment.trim());
			}
		}
	}

	handleSubmit = (e) => {
		e.preventDefault();
		if (this.state.newComment.trim()) {
			this.postComment(this.state.newComment.trim());
		}
	}

	CommentsView = () => {
		const { comments, newComment } = this.state;

		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 320, maxHeight: '70vh' }}>
				<Box
					ref={this.commentsListRef}
					sx={{
						flex: 1,
						overflowY: 'auto',
						pb: 2,
						minHeight: 180,
						scrollbarWidth: 'none',
						msOverflowStyle: 'none',
						'&::-webkit-scrollbar': {
							display: 'none'
						}
					}}
				>
					<Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
						<List dense>
						{comments.map((comment) => (
							<ListItem
								key={comment._id}
								alignItems="flex-start"
								sx={{
									py: 1.5,
									px: 0,
									display: 'flex',
									alignItems: 'flex-start'
								}}
							>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'flex-start',
										gap: 1.5,
										width: '100%'
									}}
								>
									<ListItemAvatar sx={{ minWidth: 0, m: 0 }}>
										<Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
											{comment.user?.username?.charAt(0)?.toUpperCase() || 'U'}
										</Avatar>
									</ListItemAvatar>
									<Box
										sx={{
											minWidth: 0,
											flex: 1,
											wordBreak: 'break-word',
											color: 'text.primary'
										}}
									>
										<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25, minWidth: 0 }}>
											<Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
												{comment.user?.nick_name || comment.user?.name || comment.user?.username || 'Unknown user'}
											</Typography>
											<Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
												{formatDate(comment.created_at)}
											</Typography>
										</Box>
										<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
											{this.renderMentions(comment.text)}
										</Typography>
									</Box>
								</Box>
							</ListItem>
						))}
						{comments.length === 0 && (
							<Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
								<CommentIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
								<Typography variant="body2">No comments yet. Start the conversation!</Typography>
							</Box>
						)}
						</List>
					</Box>
				</Box>

				<Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 1, flexShrink: 0 }}>
					<Box component="form" onSubmit={this.handleSubmit}>


						<MentionsTextField
							variant='outlined'
							label='Write a comment...'
							placeholder="Write a comment..."
							fullWidth
							multiline
							rows={2}
							value={newComment}
							onChange={this.handleCommentChange}
							dataSources={[
								{
									data: this.state.contacts,
								},
							]}
							slotProps={{
								suggestionsOverlay: {
									popper: {
										sx: {
											zIndex: 9999,
										},
									},
								},
							}}
							InputProps={{
								sx: { borderRadius: 2, pr: 0.5 },
								endAdornment: (
									<InputAdornment position="end" sx={{ alignSelf: 'center', mt: 0 }}>
										<IconButton
											type="submit"
											size="small"
											disabled={!newComment.trim() || this.state.sending_message}
										>
											{this.state.sending_message ? <CircularProgress size={16} /> : <SendIcon />}
										</IconButton>
									</InputAdornment>
								)
							}}
						/>
					</Box>
				</Box>
			</Box>
		);
	}

	render() {

		if (!this.props?.container?._id && !this.props?.file?._id) {
			return null;
		}

		return (
			<>
			{(this.state.view_type == 'menu') ? (
				<MenuItem
					onClick={() => {
						this.setState({
							is_modal_open: true,
							loading: true
						});
						this.fetchComments();
					}}
				>
					<ListItemIcon>
						<CommentIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>
						Comment
					</ListItemText>
				</MenuItem>
			) : null}
			{(this.state.view_type == 'icon') ? (
				<IconButton
					onClick={() => {
						this.setState({
							is_modal_open: true,
							loading: true
						});
						this.fetchComments();
					}}
					size='small'
				>
					<CommentIcon />
				</IconButton>
			) : null}
			{(this.state.view_type == 'chip') ? (
				<Chip
					variant={this.state.is_comment_visible ? "filled" : "outlined"}
					icon={<AddIcon />}
					label="Comment"
					size="small"
					sx={{ border: '0px' }}
					onClick={() => {
						this.setState({
							is_comment_visible: !this.state.is_comment_visible,
							is_modal_open: true,
							loading: true,
						})
						this.fetchComments();
					}}
				/>
			) : null}
			<Modal
				open={this.state.is_modal_open}
				onClose={() => {
					if (this.props.onClose) {
						this.props.onClose();
					}
					this.setState({
						is_modal_open: false
					});
				}}
			>
				<Grid container>
					<Grid item style={modalStyle} xs={12} md={8} lg={6}>
						<Card variant='outlined'>
							<CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
								{(this.state.loading) ? (
									<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
										<CircularProgress />
									</Box>
								) : null}
								{(!this.state.loading) ? (
									this.CommentsView()
								) : null}
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Modal>
			</>
		);
	}
}

const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '100%',
	maxWidth: 900,
	bgcolor: 'background.paper',
	boxShadow: 24,
	p: 2,
	maxHeight: '90vh',
	overflowY: 'auto'
};

export default function (props) {
	return <Comment {...props} />;
}
